import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiClient, { TokenManager } from '../../../../shared/lib/apiClient';
import { Project, ProjectCreatePayload } from '@/shared/types/project';
import LlmService from '../chat/api/llmApi';
import { useVishCoderChat } from '../chat/components/useVishCoderChat';
import {
    addChatMessage,
    ChatMessage,
    getChatMessages,
    initializeChatHistory,
    persistCurrentMessagesToProject,
    removeChatMessageById,
} from '../chat/components/sharedChatService';
import { getDiscoveryDocumentStorageKey } from '../../tabs/requirements/components/documents/LiveDiscoveryDocument';
import DiscoveryDocumentsShell from '../../tabs/requirements/components/documents/DiscoveryDocumentsShell';
import { useDiscoveryProceedPauseRegister } from './DiscoveryProceedPauseContext';
import { sendDiscoveryProceedContractToVishCoder } from './discoveryProceedContract';
import {
    DISCOVERY_GITHUB_GATE_MESSAGE,
    isGithubReadyForDiscoveryProceed,
} from './discoveryGithubSettingsGate';
import { GITHUB_SETTINGS_STORAGE_KEY } from '../../../../shared/lib/storageUtils';

const DISCOVERY_GITHUB_SHORT_HINT =
    '*Git Settings Needed.';

const DISCOVERY_PROCEED_PAUSE_MSG_TYPE = 'discovery_proceed_pause';

const DISCOVERY_PROCEED_DONE_LS_KEY = 'vishmaker_discovery_proceed_done';
/** Defer sidebar `onProjectCreated` to the next task so `persistCurrentMessagesToProject` and session setup can start first. */
const ON_PROJECT_CREATED_DEFER_MS = 0;

type DiscoveryProceedDoneMap = Record<string, 1>;

function getDiscoveryProceedDoneMap(): DiscoveryProceedDoneMap {
    try {
        const raw = localStorage.getItem(DISCOVERY_PROCEED_DONE_LS_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as unknown;
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return {};
        }
        const map: DiscoveryProceedDoneMap = {};
        for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
            if (value === 1) {
                map[key] = 1;
            }
        }
        return map;
    } catch {
        return {};
    }
}

function isDiscoveryProceedDoneMarked(projectId: string | number): boolean {
    try {
        const map = getDiscoveryProceedDoneMap();
        return map[String(projectId)] === 1;
    } catch {
        return false;
    }
}

function markDiscoveryProceedDone(projectId: string | number): void {
    try {
        const map = getDiscoveryProceedDoneMap();
        map[String(projectId)] = 1;
        localStorage.setItem(DISCOVERY_PROCEED_DONE_LS_KEY, JSON.stringify(map));
    } catch {
        /* ignore */
    }
}

interface ProductDiscoveryChatProps {
    projectName: string;
    initialPrompt: string;
    projectId?: number | string | null;
    onBack: () => void;
    onBuild: (existingProjectId?: number) => void;
    isBuilding?: boolean;
    onProjectCreated?: (projectId: number | string, projectName?: string) => void;
    onProceed?: () => void;
    /**
     * Runs `gen_userflow` for Instant Draft. When omitted, this component calls LlmService directly.
     * Parent should pass this to update project UI (e.g. flows available) after success.
     */
    onUserFlowDraft?: (prompt: string, projectId: string | number) => Promise<void>;
    /**
     * Opens Git Settings scoped to this discovery session:
     * - Pass the current in-chat project id when it exists, so settings save to that project.
     * - Pass `null` when no project exists yet — settings save to browser defaults (`githubSettings`) and still satisfy Proceed.
     */
    onRequestGitSettings?: (projectIdForModal: string | number | null) => void;
}

const SYSTEM_MESSAGE_TEXT =
    '**Guided discovery** walks through goals and flow with the agent (with Git + build hooks when you continue).\n\n' +
    '**Instant draft** generates a first user flow right away so you can iterate.\n\n' +
    'Your progress is saved either way.';

const ProductDiscoveryChat: React.FC<ProductDiscoveryChatProps> = ({
    projectName,
    initialPrompt,
    projectId,
    onBack: _onBack,
    onBuild: _onBuild,
    isBuilding: _isBuilding = false,
    onProjectCreated,
    onProceed,
    onUserFlowDraft,
    onRequestGitSettings,
}) => {
    const [currentProjectId, setCurrentProjectId] = useState<number | string | null>(projectId ?? null);
    const [hasProceeded, setHasProceeded] = useState(false);
    const [userFlowDraftInFlight, setUserFlowDraftInFlight] = useState(false);
    const [decisionPending, setDecisionPending] = useState(true);
    const [discoveryPrompt, setDiscoveryPrompt] = useState(initialPrompt);
    /** Ref for bootstrap logic only — must not be a useEffect dependency (prompt sync from parent would re-run bootstrap). */
    const discoveryPromptRef = useRef(initialPrompt);
    discoveryPromptRef.current = discoveryPrompt;
    const { chatMessages, sendMessage, agentProcessing, clearChatHistory, agentConnected } = useVishCoderChat(
        'project_chat',
        currentProjectId,
        'discovery'
    );

    useEffect(() => {
        setDiscoveryPrompt(initialPrompt);
    }, [initialPrompt]);

    useEffect(() => {
        if (projectId !== undefined && projectId !== null) {
            setCurrentProjectId(projectId);
        }
    }, [projectId]);

    useEffect(() => {
        if (hasProceeded) onProceed?.();
    }, [hasProceeded, onProceed]);

    const discoveryActionsMessageIdRef = useRef<string | null>(null);
    const delayedProjectCreatedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    /** Mirrors ref so Proceed/Pause context re-renders and the chat panel hides duplicate rows. */
    const [discoveryActionsMessageId, setDiscoveryActionsMessageId] = useState<string | null>(null);
    const initialUserMessageIdRef = useRef<string | null>(null);
    /**
     * Guard first-proceed race:
     * creating project updates id/name and can trigger history reload before first send completes.
     * During this window we should never re-show Proceed/Pause.
     */
    const proceedInFlightRef = useRef(false);
    /** User clicked Proceed; project exists (or was just created) but Git triple incomplete → open Git modal, then send discovery + build_feature when ready. */
    const pendingProceedPayloadsAfterGitRef = useRef(false);
    /** Latest title for storage key cleanup; not a bootstrap effect dependency (name sync after create must not re-inject). */
    const projectNameRef = useRef(projectName);
    projectNameRef.current = projectName;
    const { register: registerDiscoveryProceedPause } = useDiscoveryProceedPauseRegister();

    const [githubSettingsReady, setGithubSettingsReady] = useState(false);
    const [githubSettingsGateResolved, setGithubSettingsGateResolved] = useState(false);
    const refreshGithubSettingsReady = useCallback(async () => {
        const ok = await isGithubReadyForDiscoveryProceed(currentProjectId ?? projectId ?? null);
        setGithubSettingsReady(ok);
        setGithubSettingsGateResolved(true);
    }, [currentProjectId, projectId]);

    useEffect(() => {
        let cancelled = false;
        setGithubSettingsGateResolved(false);
        void (async () => {
            const ok = await isGithubReadyForDiscoveryProceed(currentProjectId ?? projectId ?? null);
            if (!cancelled) {
                setGithubSettingsReady(ok);
                setGithubSettingsGateResolved(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [currentProjectId, projectId]);

    useEffect(() => {
        if (!decisionPending || hasProceeded) return;
        const id = window.setInterval(() => {
            void refreshGithubSettingsReady();
        }, 2000);
        return () => window.clearInterval(id);
    }, [decisionPending, hasProceeded, refreshGithubSettingsReady]);

    useEffect(() => {
        const onFocus = () => {
            void refreshGithubSettingsReady();
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [refreshGithubSettingsReady]);

    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key === GITHUB_SETTINGS_STORAGE_KEY) {
                void refreshGithubSettingsReady();
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [refreshGithubSettingsReady]);

    useEffect(() => {
        const onSaved = () => {
            void refreshGithubSettingsReady();
        };
        window.addEventListener('vishmaker-git-settings-saved', onSaved);
        return () => window.removeEventListener('vishmaker-git-settings-saved', onSaved);
    }, [refreshGithubSettingsReady]);

    useEffect(() => {
        return () => {
            if (delayedProjectCreatedTimerRef.current) {
                clearTimeout(delayedProjectCreatedTimerRef.current);
                delayedProjectCreatedTimerRef.current = null;
            }
        };
    }, []);

    // 1. Bootstrap discovery chat: new project clears history; existing project loads session and skips reset if messages exist
    useEffect(() => {
        let cancelled = false;

        const injectNewProjectBootstrap = () => {
            clearChatHistory();
            try {
                localStorage.removeItem(getDiscoveryDocumentStorageKey(undefined, projectNameRef.current));
            } catch {
                /* ignore */
            }
            setHasProceeded(false);
            setDecisionPending(true);
            discoveryActionsMessageIdRef.current = null;
            setDiscoveryActionsMessageId(null);
            initialUserMessageIdRef.current = null;
            const promptText = discoveryPromptRef.current.trim();
            if (!promptText) return;

            const userMsg: ChatMessage = {
                id: `initial-user-${Date.now()}`,
                type: 'user',
                content: promptText,
                timestamp: new Date(),
                page: 'project_chat',
                channel: 'discovery',
            };
            initialUserMessageIdRef.current = userMsg.id;
            const systemMsg: ChatMessage = {
                id: `initial-system-${Date.now()}`,
                type: 'assistant',
                content: SYSTEM_MESSAGE_TEXT,
                timestamp: new Date(),
                page: 'project_chat',
                channel: 'discovery',
            };
            const actionsId = `discovery-actions-${Date.now()}`;
            discoveryActionsMessageIdRef.current = actionsId;
            setDiscoveryActionsMessageId(actionsId);
            const actionsMsg: ChatMessage = {
                id: actionsId,
                type: 'assistant',
                content: 'Pick your next step:',
                timestamp: new Date(),
                page: 'project_chat',
                channel: 'discovery',
                msgType: DISCOVERY_PROCEED_PAUSE_MSG_TYPE,
                discovery: { msg_type: DISCOVERY_PROCEED_PAUSE_MSG_TYPE },
            };
            addChatMessage(userMsg);
            addChatMessage(systemMsg);
            addChatMessage(actionsMsg);
        };

        const dedupeDiscoveryProceedPauseRows = (rows: ChatMessage[]) => {
            const pauseRows = rows.filter((m) => m.msgType === DISCOVERY_PROCEED_PAUSE_MSG_TYPE);
            if (pauseRows.length <= 1) return;
            for (let i = 1; i < pauseRows.length; i++) {
                removeChatMessageById(pauseRows[i].id);
            }
        };

        const resumeExistingProject = async (pid: number | string) => {
            setHasProceeded(false);
            setDecisionPending(true);
            discoveryActionsMessageIdRef.current = null;
            setDiscoveryActionsMessageId(null);
            initialUserMessageIdRef.current = null;

            await initializeChatHistory(pid);
            if (cancelled) return;

            const existing = getChatMessages('project_chat', 'discovery');
            dedupeDiscoveryProceedPauseRows(existing);
            if (isDiscoveryProceedDoneMarked(pid)) {
                getChatMessages('project_chat', 'discovery')
                    .filter((m) => m.msgType === DISCOVERY_PROCEED_PAUSE_MSG_TYPE)
                    .forEach((m) => removeChatMessageById(m.id));
                discoveryActionsMessageIdRef.current = null;
                setDiscoveryActionsMessageId(null);
                setDecisionPending(false);
                setHasProceeded(true);
                return;
            }
            if (proceedInFlightRef.current) {
                existing
                    .filter((m) => m.msgType === DISCOVERY_PROCEED_PAUSE_MSG_TYPE)
                    .forEach((m) => removeChatMessageById(m.id));
                discoveryActionsMessageIdRef.current = null;
                setDiscoveryActionsMessageId(null);
                setDecisionPending(false);
                setHasProceeded(true);
                return;
            }
            const firstUser = getChatMessages('project_chat', 'discovery').find((m) => m.type === 'user');
            if (firstUser) {
                initialUserMessageIdRef.current = firstUser.id;
            }

            const existingAfterDedupe = getChatMessages('project_chat', 'discovery');
            if (existingAfterDedupe.length > 0) {
                const actionsRow = existingAfterDedupe.find((m) => m.msgType === DISCOVERY_PROCEED_PAUSE_MSG_TYPE);
                const hasPaused = existingAfterDedupe.some((m) =>
                    m.type === 'assistant' && m.content.includes("paused for now")
                );
                /**
                 * Proceed/Pause row is removed locally on click but may still exist in the session in DynamoDB.
                 * If any assistant message appears after that row (and it is not the pause copy), the user already continued.
                 */
                if (hasPaused) {
                    discoveryActionsMessageIdRef.current = null;
                    setDiscoveryActionsMessageId(null);
                    setDecisionPending(false);
                    setHasProceeded(false);
                } else if (actionsRow) {
                    const actionsIdx = existingAfterDedupe.findIndex((m) => m.id === actionsRow.id);
                    const afterActions = actionsIdx >= 0 ? existingAfterDedupe.slice(actionsIdx + 1) : [];
                    const hasContinuedAfterDecision = afterActions.some((m) => {
                        if (m.msgType === DISCOVERY_PROCEED_PAUSE_MSG_TYPE) return false;
                        if (m.type === 'status') return true;
                        if (m.type === 'assistant' && m.content.includes('paused for now')) return false;
                        if (m.type === 'assistant') return true;
                        return false;
                    });
                    if (hasContinuedAfterDecision) {
                        removeChatMessageById(actionsRow.id);
                        discoveryActionsMessageIdRef.current = null;
                        setDiscoveryActionsMessageId(null);
                        setDecisionPending(false);
                        setHasProceeded(true);
                    } else {
                        discoveryActionsMessageIdRef.current = actionsRow.id;
                        setDiscoveryActionsMessageId(actionsRow.id);
                        setDecisionPending(true);
                        setHasProceeded(false);
                    }
                } else {
                    setDecisionPending(false);
                    setHasProceeded(true);
                }
                return;
            }

            const resumePrompt = discoveryPromptRef.current.trim();
            if (!resumePrompt) return;

            const userMsg: ChatMessage = {
                id: `initial-user-${Date.now()}`,
                type: 'user',
                content: resumePrompt,
                timestamp: new Date(),
                page: 'project_chat',
                channel: 'discovery',
            };
            initialUserMessageIdRef.current = userMsg.id;
            const systemMsg: ChatMessage = {
                id: `initial-system-${Date.now()}`,
                type: 'assistant',
                content: SYSTEM_MESSAGE_TEXT,
                timestamp: new Date(),
                page: 'project_chat',
                channel: 'discovery',
            };
            const actionsId = `discovery-actions-${Date.now()}`;
            discoveryActionsMessageIdRef.current = actionsId;
            setDiscoveryActionsMessageId(actionsId);
            const actionsMsg: ChatMessage = {
                id: actionsId,
                type: 'assistant',
                content: 'Pick your next step:',
                timestamp: new Date(),
                page: 'project_chat',
                channel: 'discovery',
                msgType: DISCOVERY_PROCEED_PAUSE_MSG_TYPE,
                discovery: { msg_type: DISCOVERY_PROCEED_PAUSE_MSG_TYPE },
            };
            addChatMessage(userMsg);
            addChatMessage(systemMsg);
            addChatMessage(actionsMsg);
        };

        const effectiveProjectKey = projectId ?? currentProjectId;
        const hasProjectKey =
            effectiveProjectKey !== undefined &&
            effectiveProjectKey !== null &&
            String(effectiveProjectKey).trim() !== '';

        if (hasProjectKey) {
            resumeExistingProject(effectiveProjectKey);
        } else {
            injectNewProjectBootstrap();
        }

        return () => {
            cancelled = true;
        };
    }, [clearChatHistory, projectId, currentProjectId]);

    const scheduleNotifyProjectCreated = useCallback(
        (finalProjectId: string | number, createdName: string | undefined) => {
            if (delayedProjectCreatedTimerRef.current) {
                clearTimeout(delayedProjectCreatedTimerRef.current);
            }
            delayedProjectCreatedTimerRef.current = setTimeout(() => {
                onProjectCreated?.(finalProjectId, createdName);
                delayedProjectCreatedTimerRef.current = null;
            }, ON_PROJECT_CREATED_DEFER_MS);
        },
        [onProjectCreated]
    );

    /**
     * 1) build_feature: streaming contract (VishCoder build channel)
     * 2) discovery: same initial prompt so backend can emit `index` / doc_summary → LiveDiscoveryDocument via chatMessages + phase model
     */
    const sendDiscoveryAndBuildPayloads = useCallback(
        async (finalProjectId: string | number) => {
            if (agentConnected) {
                const contractSent = await sendDiscoveryProceedContractToVishCoder(finalProjectId);
                if (!contractSent) {
                    addChatMessage({
                        id: `discovery-contract-err-${Date.now()}`,
                        type: 'assistant',
                        content:
                            '⚠️ Could not send the build contract to VishCoder. Check your connection, then try Guided Discovery again.',
                        timestamp: new Date(),
                        page: 'project_chat',
                        channel: 'discovery',
                    });
                }
            }

            await sendMessage(discoveryPrompt, undefined, {
                skipUserBubble: true,
                existingUserMessageId: initialUserMessageIdRef.current ?? undefined,
                overrideProjectId: finalProjectId,
            });

            markDiscoveryProceedDone(finalProjectId);
            setHasProceeded(true);
            void refreshGithubSettingsReady();
        },
        [agentConnected, discoveryPrompt, refreshGithubSettingsReady, sendMessage]
    );

    const handleProceedNow = useCallback(async () => {
        if (hasProceeded || agentProcessing || userFlowDraftInFlight || !githubSettingsGateResolved || !discoveryPrompt.trim())
            return;

        const aid = discoveryActionsMessageIdRef.current;
        if (aid) {
            removeChatMessageById(aid);
            discoveryActionsMessageIdRef.current = null;
            setDiscoveryActionsMessageId(null);
        }
        setDecisionPending(false);
        proceedInFlightRef.current = true;
        pendingProceedPayloadsAfterGitRef.current = false;

        try {
            let finalProjectId = currentProjectId ?? projectId;
            if (!finalProjectId) {
                const userId = TokenManager.getUserId();
                if (!userId) throw new Error('User ID not found. Please sign in again.');

                const project = await apiClient<Project>('/projects', {
                    method: 'POST',
                    body: {
                        name: projectName.trim() || 'New Project',
                        initial_prompt: discoveryPrompt.trim(),
                        user_id: userId,
                    } as ProjectCreatePayload,
                });

                finalProjectId = project.id;
                setCurrentProjectId(finalProjectId);
                persistCurrentMessagesToProject(finalProjectId, 'project_chat', 'discovery');
                scheduleNotifyProjectCreated(finalProjectId, project.name);
            }

            const pid = finalProjectId as string | number;
            const gitOk = await isGithubReadyForDiscoveryProceed(pid);

            if (!gitOk) {
                pendingProceedPayloadsAfterGitRef.current = true;
                if (onRequestGitSettings) {
                    onRequestGitSettings(pid);
                } else {
                    addChatMessage({
                        id: `git-required-${Date.now()}`,
                        type: 'assistant',
                        content: DISCOVERY_GITHUB_GATE_MESSAGE,
                        timestamp: new Date(),
                        page: 'project_chat',
                        channel: 'discovery',
                    });
                }
                proceedInFlightRef.current = false;
                void refreshGithubSettingsReady();
                return;
            }

            await sendDiscoveryAndBuildPayloads(pid);
            proceedInFlightRef.current = false;
        } catch (error: unknown) {
            console.error('Error in discovery proceed:', error);
            pendingProceedPayloadsAfterGitRef.current = false;
            const message = error instanceof Error ? error.message : 'Sorry, I encountered an error. Please try again.';
            addChatMessage({
                id: `error-${Date.now()}`,
                type: 'assistant',
                content: message,
                timestamp: new Date(),
                page: 'project_chat',
                channel: 'discovery',
            });
            proceedInFlightRef.current = false;
            void refreshGithubSettingsReady();
        }
    }, [
        agentProcessing,
        currentProjectId,
        discoveryPrompt,
        githubSettingsGateResolved,
        hasProceeded,
        onRequestGitSettings,
        projectId,
        projectName,
        refreshGithubSettingsReady,
        scheduleNotifyProjectCreated,
        sendDiscoveryAndBuildPayloads,
        userFlowDraftInFlight,
    ]);

    useEffect(() => {
        if (!pendingProceedPayloadsAfterGitRef.current) return;
        if (hasProceeded) return;
        if (!githubSettingsGateResolved || !githubSettingsReady) return;

        const pid = currentProjectId ?? projectId;
        if (pid == null || String(pid).trim() === '') return;
        if (proceedInFlightRef.current) return;

        let cancelled = false;
        void (async () => {
            if (!(await isGithubReadyForDiscoveryProceed(pid))) return;
            if (cancelled) return;

            proceedInFlightRef.current = true;
            try {
                await sendDiscoveryAndBuildPayloads(pid);
                pendingProceedPayloadsAfterGitRef.current = false;
            } catch (e: unknown) {
                console.error('Deferred discovery proceed failed:', e);
                pendingProceedPayloadsAfterGitRef.current = false;
                const msg = e instanceof Error ? e.message : 'Could not continue after Git settings.';
                addChatMessage({
                    id: `error-deferred-${Date.now()}`,
                    type: 'assistant',
                    content: msg,
                    timestamp: new Date(),
                    page: 'project_chat',
                    channel: 'discovery',
                });
            } finally {
                proceedInFlightRef.current = false;
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        currentProjectId,
        githubSettingsGateResolved,
        githubSettingsReady,
        hasProceeded,
        projectId,
        sendDiscoveryAndBuildPayloads,
    ]);

    const handleInstantDraft = useCallback(async () => {
        if (!decisionPending) return;
        if (agentProcessing || userFlowDraftInFlight) return;

        const aid = discoveryActionsMessageIdRef.current;
        if (aid) {
            removeChatMessageById(aid);
            discoveryActionsMessageIdRef.current = null;
            setDiscoveryActionsMessageId(null);
        }
        setDecisionPending(false);

        const text = discoveryPrompt.trim();
        if (!text) return;

        setUserFlowDraftInFlight(true);
        proceedInFlightRef.current = true;

        try {
            let finalProjectId = currentProjectId ?? projectId;
            if (!finalProjectId) {
                const userId = TokenManager.getUserId();
                if (!userId) throw new Error('User ID not found. Please sign in again.');

                const project = await apiClient<Project>('/projects', {
                    method: 'POST',
                    body: {
                        name: projectName.trim() || 'New Project',
                        initial_prompt: text,
                        user_id: userId,
                    } as ProjectCreatePayload,
                });

                finalProjectId = project.id;
                setCurrentProjectId(finalProjectId);
                persistCurrentMessagesToProject(finalProjectId, 'project_chat', 'discovery');
                scheduleNotifyProjectCreated(finalProjectId, project.name);
            }

            const pid = finalProjectId as string | number;

            addChatMessage({
                id: `instant-draft-start-${Date.now()}`,
                type: 'assistant',
                content: 'Generating your user flow…',
                timestamp: new Date(),
                page: 'project_chat',
                channel: 'discovery',
            });

            if (onUserFlowDraft) {
                await onUserFlowDraft(text, pid);
            } else {
                await LlmService.processWithLlm('gen_userflow', text, String(pid), true, () => {});
            }
        } catch (error: unknown) {
            console.error('Instant draft failed:', error);
            const message = error instanceof Error ? error.message : 'Failed to generate user flow. Please try again.';
            addChatMessage({
                id: `instant-draft-err-${Date.now()}`,
                type: 'assistant',
                content: message,
                timestamp: new Date(),
                page: 'project_chat',
                channel: 'discovery',
            });
        } finally {
            setUserFlowDraftInFlight(false);
            proceedInFlightRef.current = false;
            void refreshGithubSettingsReady();
        }
    }, [
        agentProcessing,
        currentProjectId,
        decisionPending,
        discoveryPrompt,
        onUserFlowDraft,
        projectId,
        projectName,
        refreshGithubSettingsReady,
        scheduleNotifyProjectCreated,
    ]);

    useEffect(() => {
        registerDiscoveryProceedPause({
            decisionPending,
            agentProcessing: agentProcessing || userFlowDraftInFlight,
            onGuidedDiscovery: handleProceedNow,
            onInstantDraft: handleInstantDraft,
            discoveryActionsMessageId,
            githubSettingsReady,
            githubSettingsGateResolved,
            githubProceedAllowed: githubSettingsGateResolved,
            githubSettingsHint: DISCOVERY_GITHUB_SHORT_HINT,
        });
        return () => registerDiscoveryProceedPause(null);
    }, [
        agentProcessing,
        decisionPending,
        discoveryActionsMessageId,
        githubSettingsReady,
        githubSettingsGateResolved,
        handleInstantDraft,
        handleProceedNow,
        registerDiscoveryProceedPause,
        userFlowDraftInFlight,
    ]);

    return (
        <DiscoveryDocumentsShell
            projectName={projectName}
            projectId={currentProjectId ?? projectId ?? null}
            chatMessages={chatMessages}
        />
    );
};

export default ProductDiscoveryChat;
